import {
  AUTO_ROUTE_LIMITS,
  CONE_ANGLE_LIMITS,
  MAX_CAUTION_DEPTH_M,
  MAX_SIMULATED_SPEED_MS,
  type Settings,
} from '../storage/settings';
import {
  DEPTH_UNITS,
  DISTANCE_UNITS,
  METRES_PER_NAUTICAL_MILE,
  SPEED_UNITS,
  UNIT_LABELS,
  VOLUME_UNITS,
  depthToMetres,
  formatDepth,
  formatDistance,
  metresToDepth,
  msToSpeed,
  speedToMs,
  volumeToLitres,
  litresToVolume,
} from '../core/units';
import { safetyDepth, validateBoatProfile } from '../core/boat';
import { LEGAL_SECTIONS } from '../core/legal';
import { WEBCAMS } from '../core/webcams';
import { HARBOURS } from '../core/harbours';
import { PREDICTION_HORIZONS_S, formatHorizon } from '../core/predict';
import { FORWARD_CONE_ANGLE_DEG } from '../core/forward';
import { COLLISION_LIMITS, MAX_STAGE_TIME_S, TINT_STYLES } from '../core/proximity';

export interface SettingsPanelProps {
  settings: Settings;
  onChange: (settings: Settings) => void;
  onClose: () => void;
  /**
   * Play one click. Wired to the live alarm rather than to a throwaway sound,
   * because this button is also the user gesture that unblocks the audio
   * context — testing a different context would prove nothing about the one
   * that has to make a noise underway.
   */
  onTestSound?: () => void;
  /** What the alarm can actually do here. See src/ui/proximityAlarm.ts. */
  alarm?: { supported: boolean; blocked: boolean };
}

type Tab = 'boat' | 'units' | 'routing' | 'display' | 'alerts' | 'debug' | 'legal';

const TAB_LABELS: Record<Tab, string> = {
  boat: 'Boat',
  units: 'Units',
  routing: 'Routing',
  display: 'Display',
  alerts: 'Alerts',
  debug: 'Debug',
  legal: 'Legal',
};

/**
 * Settings.
 *
 * Grouped by what the skipper is thinking about rather than by where the value
 * lives in code. Boat first because it is the one that changes the chart.
 */
export function SettingsPanel({
  settings,
  onChange,
  onClose,
  onTestSound,
  alarm,
}: SettingsPanelProps): React.JSX.Element {
  const { boat, units } = settings;
  const problems = validateBoatProfile(boat);

  const patchBoat = (patch: Partial<typeof boat>): void =>
    onChange({ ...settings, boat: { ...boat, ...patch } });
  const patchAlerts = (patch: Partial<Settings['alerts']>): void =>
    onChange({ ...settings, alerts: { ...settings.alerts, ...patch } });
  /**
   * Edit one corner of the alert curve.
   *
   * Stored raw, exactly as typed. Sorting and the no-dips repair happen in
   * `normaliseStages` when the curve is *read*, not here — doing it on every
   * keystroke would reorder the rows under the skipper's finger mid-edit, which
   * is how a settings screen becomes unusable.
   */
  const patchCollision = (patch: Partial<Settings['alerts']['collision']>): void =>
    patchAlerts({ collision: { ...settings.alerts.collision, ...patch } });
  const patchStage = (index: number, patch: Partial<Settings['alerts']['stages'][number]>): void =>
    patchAlerts({
      stages: settings.alerts.stages.map(
        (stage, i) => (i === index ? { ...stage, ...patch } : stage),
      ),
    });
  const patchUnits = (patch: Partial<typeof units>): void =>
    onChange({ ...settings, units: { ...units, ...patch } });
  const patchRouting = (patch: Partial<Settings['autoRoute']>): void =>
    onChange({ ...settings, autoRoute: { ...settings.autoRoute, ...patch } });
  const patchDebug = (patch: Partial<Settings['debug']>): void =>
    onChange({ ...settings, debug: { ...settings.debug, ...patch } });

  const tab = settings.settingsTab ?? 'boat';
  const setTab = (next: Tab): void => onChange({ ...settings, settingsTab: next });

  return (
    <div className="sheet sheet--settings" role="dialog" aria-label="Settings">
      <div className="sheet__header">
        <h2>Settings</h2>
        <button type="button" onClick={onClose} aria-label="Close">✕</button>
      </div>

      <div className="tabs" role="tablist">
        {(['boat', 'units', 'routing', 'display', 'alerts', 'debug', 'legal'] as const).map((name) => (
          <button
            key={name}
            type="button"
            role="tab"
            aria-selected={tab === name}
            className={tab === name ? 'tab tab--on' : 'tab'}
            onClick={() => setTab(name)}
          >
            {TAB_LABELS[name]}
          </button>
        ))}
      </div>

      {tab === 'boat' && (
        <>
          <p className="sheet__hint">
            Draft and margin set the safety depth. The chart re-shades and hazards
            shallower than this are marked at every zoom.
          </p>

          <label className="field">
            <span className="field__label">Boat name</span>
            <input
              type="text"
              value={boat.name}
              onChange={(e) => patchBoat({ name: e.target.value })}
            />
          </label>

          <NumberField
            label="Draft"
            value={metresToDepth(boat.draftM, units.depth)}
            unit={UNIT_LABELS[units.depth]}
            step={units.depth === 'ft' ? 0.5 : 0.1}
            min={0.1}
            onChange={(v) => patchBoat({ draftM: depthToMetres(v, units.depth) })}
          />
          <NumberField
            label="Under-keel margin"
            value={metresToDepth(boat.safetyMarginM, units.depth)}
            unit={UNIT_LABELS[units.depth]}
            step={units.depth === 'ft' ? 0.5 : 0.1}
            min={0}
            onChange={(v) => patchBoat({ safetyMarginM: depthToMetres(v, units.depth) })}
          />
          <NumberField
            label="Air draft"
            hint="Highest fixed point. Leave blank if nothing to hit."
            value={boat.airDraftM === undefined ? '' : metresToDepth(boat.airDraftM, units.depth)}
            unit={UNIT_LABELS[units.depth]}
            step={units.depth === 'ft' ? 0.5 : 0.1}
            min={0}
            allowEmpty
            onChange={(v) => patchBoat({
              airDraftM: v === null ? undefined : depthToMetres(v, units.depth),
            })}
          />

          <div className="sheet__summary">
            Safety depth <strong>{formatDepth(safetyDepth(boat), units.depth)}</strong>
          </div>

          <NumberField
            label="Cruise speed"
            value={msToSpeed(boat.cruiseSpeedMs, units.speed)}
            unit={UNIT_LABELS[units.speed]}
            step={0.5}
            min={0.1}
            onChange={(v) => patchBoat({ cruiseSpeedMs: speedToMs(v, units.speed) })}
          />
          <NumberField
            label="Fuel burn at cruise"
            value={litresToVolume(boat.fuelBurnLph, units.volume)}
            unit={`${UNIT_LABELS[units.volume]}/h`}
            step={0.5}
            min={0}
            onChange={(v) => patchBoat({ fuelBurnLph: volumeToLitres(v, units.volume) })}
          />
          <NumberField
            label="Fuel capacity"
            hint="Leave blank if you would rather not see range estimates."
            value={boat.fuelCapacityL === undefined ? '' : litresToVolume(boat.fuelCapacityL, units.volume)}
            unit={UNIT_LABELS[units.volume]}
            step={5}
            min={0}
            allowEmpty
            onChange={(v) => patchBoat({
              fuelCapacityL: v === null ? undefined : volumeToLitres(v, units.volume),
            })}
          />
          <NumberField
            label="Fuel reserve"
            value={Math.round(boat.fuelReserveFraction * 100)}
            unit="%"
            step={5}
            min={0}
            max={90}
            onChange={(v) => patchBoat({ fuelReserveFraction: v / 100 })}
          />

          {problems.length > 0 && (
            <ul className="settings__problems" role="alert">
              {problems.map((problem) => <li key={problem.field}>{problem.message}</li>)}
            </ul>
          )}
        </>
      )}

      {tab === 'units' && (
        <>
          <p className="sheet__hint">
            Each is independent — nautical miles with metric depths is a normal
            Canadian setup. Charts are stored metric and converted for display, so
            changing these never reloads anything.
          </p>
          <UnitChoice
            label="Distance"
            options={DISTANCE_UNITS}
            value={units.distance}
            onChange={(distance) => patchUnits({ distance })}
          />
          <UnitChoice
            label="Speed"
            options={SPEED_UNITS}
            value={units.speed}
            onChange={(speed) => patchUnits({ speed })}
          />
          <UnitChoice
            label="Depth"
            options={DEPTH_UNITS}
            value={units.depth}
            onChange={(depth) => patchUnits({ depth })}
          />
          <UnitChoice
            label="Fuel"
            options={VOLUME_UNITS}
            value={units.volume}
            onChange={(volume) => patchUnits({ volume })}
          />
        </>
      )}

      {tab === 'routing' && (
        <>
          <p className="sheet__hint">
            Route suggestion plans through charted water deep enough for your boat.
            It is a suggestion — always check it before using it, and it can only
            see chart data that is loaded.
          </p>

          <NumberField
            label="Planning grid"
            hint="Smaller finds narrower channels but takes longer to search."
            value={settings.autoRoute.cellSizeM}
            unit="m"
            step={10}
            min={AUTO_ROUTE_LIMITS.cellSizeM.min}
            max={AUTO_ROUTE_LIMITS.cellSizeM.max}
            onChange={(cellSizeM) => patchRouting({ cellSizeM })}
          />
          <NumberField
            label="Stand off shore"
            hint="How far the suggestion keeps from shoal water, in grid cells."
            value={settings.autoRoute.shoreClearanceCells}
            unit="cells"
            step={1}
            min={AUTO_ROUTE_LIMITS.shoreClearanceCells.min}
            max={AUTO_ROUTE_LIMITS.shoreClearanceCells.max}
            onChange={(shoreClearanceCells) => patchRouting({ shoreClearanceCells })}
          />

          <p className="sheet__hint sheet__hint--small">
            About {Math.round(settings.autoRoute.cellSizeM * settings.autoRoute.shoreClearanceCells)} m
            of clearance at the current settings.
          </p>
        </>
      )}

      {tab === 'display' && (
        <>
          <Toggle
            label="Spot soundings"
            checked={settings.showSoundings}
            onChange={(showSoundings) => onChange({ ...settings, showSoundings })}
          />
          <Toggle
            label="Place names and labels"
            checked={settings.showLabels}
            onChange={(showLabels) => onChange({ ...settings, showLabels })}
          />
          <Toggle
            label="Webcams"
            checked={settings.showWebcams}
            onChange={(showWebcams) => onChange({ ...settings, showWebcams })}
          />
          <p className="sheet__hint sheet__hint--small">
            {WEBCAMS.length} known cameras, mostly harbours and lighthouses.
            Third-party sites, not chart data, and they need a connection.
          </p>
          <Toggle
            label="Harbours and marinas"
            checked={settings.showHarbours}
            onChange={(showHarbours) => onChange({ ...settings, showHarbours })}
          />
          <p className="sheet__hint sheet__hint--small">
            {HARBOURS.length} harbours, wharves and marinas, linking to photos
            on marinas.com. Positions from Fisheries and Oceans Canada and
            OpenStreetMap — registries, not surveys.
          </p>
          <Toggle
            label="Predicted track"
            checked={settings.prediction.enabled}
            onChange={(enabled) => onChange({
              ...settings,
              prediction: { ...settings.prediction, enabled },
            })}
          />
          {settings.prediction.enabled && (
            <>
              <div className="horizons" role="group" aria-label="Prediction horizons">
                {PREDICTION_HORIZONS_S.map((seconds) => {
                  const on = settings.prediction.horizonsS.includes(seconds);
                  return (
                    <button
                      key={seconds}
                      type="button"
                      className={on ? 'horizon horizon--on' : 'horizon'}
                      aria-pressed={on}
                      onClick={() => onChange({
                        ...settings,
                        prediction: {
                          ...settings.prediction,
                          horizonsS: on
                            ? settings.prediction.horizonsS.filter((s) => s !== seconds)
                            : [...settings.prediction.horizonsS, seconds].sort((a, b) => a - b),
                        },
                      })}
                    >
                      {formatHorizon(seconds)}
                    </button>
                  );
                })}
              </div>
              <p className="sheet__hint sheet__hint--small">
                {settings.prediction.horizonsS.length === 0
                  ? 'No horizons selected, so nothing is drawn.'
                  : `Dashed line to where you would be in ${
                    formatHorizon(Math.max(...settings.prediction.horizonsS))
                  } at the current speed and course.`}
              </p>
              <p className="sheet__hint sheet__hint--small">
                Dead reckoning only. It assumes you hold this course and speed,
                and it is <strong>not checked against the chart</strong> — a
                line that looks clear is not a statement that it is.
              </p>
            </>
          )}
          <Toggle
            label="Night colours"
            checked={settings.palette === 'night'}
            onChange={(night) => onChange({ ...settings, palette: night ? 'night' : 'day' })}
          />
          <Toggle
            label="Keep screen awake"
            checked={settings.keepScreenAwake}
            onChange={(keepScreenAwake) => onChange({ ...settings, keepScreenAwake })}
          />
          <p className="sheet__hint sheet__hint--small">
            Hazards and depth shading cannot be turned off. They are the reason
            this is a chart rather than a map.
          </p>
        </>
      )}

      {tab === 'alerts' && (
        <>
          <p className="sheet__hint">
            What is in the water ahead, and how you are told about it. The beam
            below decides what counts as “ahead” for every one of these — the
            marks on the chart, the banner, the click track and the colour.
          </p>
          <Toggle
            label="Mark dangers ahead"
            checked={settings.dangersAhead}
            onChange={(dangersAhead) => onChange({ ...settings, dangersAhead })}
          />
          <p className="sheet__hint sheet__hint--small">
            Rings charted rocks, wrecks and obstructions lying inside the beam
            ahead of you, out as far as the predicted track reaches. This is the
            magenta symbol that used to sit on every such hazard on the chart,
            wherever it was.
          </p>
          <p className="sheet__hint sheet__hint--small">
            It reads the chart, unlike the track itself — but only the part of it
            that is loaded and on screen. <strong>Nothing marked is not the same
            as nothing there.</strong> Every charted danger still carries its own
            small mark whether or not it is ahead of you.
          </p>

          {settings.dangersAhead && (
            <>
              <NumberField
                label="Beam width"
                hint="Full width of the sector searched ahead — half either side of your course."
                value={Math.round(settings.coneAngleDeg)}
                unit="°"
                step={5}
                min={CONE_ANGLE_LIMITS.min}
                max={CONE_ANGLE_LIMITS.max}
                onChange={(coneAngleDeg) => onChange({ ...settings, coneAngleDeg })}
              />
              <p className="sheet__hint sheet__hint--small">
                About{' '}
                {formatDistance(
                  2 * METRES_PER_NAUTICAL_MILE
                    * Math.sin((settings.coneAngleDeg / 2) * (Math.PI / 180)),
                  units.distance,
                )}{' '}
                across at a mile ahead. Narrower marks fewer things and each one
                is more likely to matter; wider misses less. The default{' '}
                {FORWARD_CONE_ANGLE_DEG}° is about what a small craft wanders
                through in a seaway while nominally steering straight.
              </p>
              <p className="sheet__hint sheet__hint--small">
                <strong>This is one beam, not two.</strong> It sets what gets a
                mark on the chart, what the banner counts, and what the click
                track and the alert colour react to — a chart that ringed a rock
                its own alarm ignored would be worse than either alone.
              </p>

              <NumberField
                label="Call water shoal below"
                hint="A quieter mark for water you can float in but would rather know about. Zero turns it off."
                value={metresToDepth(settings.cautionDepthM, units.depth)}
                unit={UNIT_LABELS[units.depth]}
                step={units.depth === 'ft' ? 1 : 0.5}
                min={0}
                max={metresToDepth(MAX_CAUTION_DEPTH_M, units.depth)}
                onChange={(v) => onChange({
                  ...settings,
                  cautionDepthM: depthToMetres(v, units.depth),
                })}
              />
              <p className="sheet__hint sheet__hint--small">
                Separate from your safety depth
                ({formatDepth(safetyDepth(boat), units.depth)}), and never drawn
                in the danger colour. Ives Knoll is charted 2–5 m in 20–30 m of
                harbour: a boat drawing a metre can float over it, which is why
                the danger ring stays off it — and it is still worth seeing at
                fifteen knots.
              </p>

              <h3 className="sheet__section">Proximity alert</h3>
              <p className="sheet__hint sheet__hint--small">
                Sound and colour raised by the same marks the chart is already
                showing, so you can tell how close the nearest one is without
                looking. Both read one strength, set by the curve at the bottom
                of this section — by default barely anything until a minute out,
                then hard.
              </p>

              <Toggle
                label="Click as you close"
                checked={settings.alerts.sound}
                onChange={(sound) => patchAlerts({ sound })}
              />
              {settings.alerts.sound && (
                <>
                  <RangeField
                    label="Volume"
                    value={settings.alerts.volume}
                    display={`${Math.round(settings.alerts.volume * 100)}%`}
                    min={0}
                    max={1}
                    step={0.05}
                    onChange={(volume) => patchAlerts({ volume })}
                  />
                  <button
                    type="button"
                    className="route__check-button route__check-button--secondary"
                    onClick={onTestSound}
                    disabled={!onTestSound || alarm?.supported === false}
                  >
                    Test sound
                  </button>
                  {alarm?.supported === false && (
                    <p className="sheet__hint sheet__hint--small" role="alert">
                      This browser has no audio support, so the click cannot
                      play. The marks on the chart and the banner are unaffected.
                    </p>
                  )}
                  {alarm?.supported !== false && alarm?.blocked && (
                    <p className="sheet__hint sheet__hint--small" role="alert">
                      The browser has not allowed sound yet. Tap <strong>Test
                      sound</strong> once — until you do, the alert is silent
                      whatever is ahead.
                    </p>
                  )}
                  <p className="sheet__hint sheet__hint--small">
                    A rising click rate, like a Geiger counter: the rate is the
                    message, not the sound. Shoal cautions click lower and
                    slower and never reach the danger rate.
                  </p>
                </>
              )}

              <Toggle
                label="Show the alert as a colour"
                checked={settings.alerts.tint}
                onChange={(tint) => patchAlerts({ tint })}
              />
              {settings.alerts.tint && (
                <>
                  <div className="field">
                    <span className="field__label">Where</span>
                    <div className="unit-choice" role="radiogroup" aria-label="Where">
                      {TINT_STYLES.map((style) => (
                        <button
                          key={style}
                          type="button"
                          role="radio"
                          aria-checked={settings.alerts.tintStyle === style}
                          className={settings.alerts.tintStyle === style
                            ? 'unit-choice__option unit-choice__option--on'
                            : 'unit-choice__option'}
                          onClick={() => patchAlerts({ tintStyle: style })}
                        >
                          {style === 'bar' ? 'Bar at the top' : 'Edges of the chart'}
                        </button>
                      ))}
                    </div>
                  </div>
                  <p className="sheet__hint sheet__hint--small">
                    {settings.alerts.tintStyle === 'bar'
                      ? 'A strip across the very top, over no chart at all — it '
                        + 'cannot shift a single charted colour, which is why it is '
                        + 'the default.'
                      : 'Colour creeping in from the edges. The middle keeps its '
                        + 'true chart colours, but the outer part of the chart does '
                        + 'shift — worth knowing if you read depth shading near the '
                        + 'edge of the screen.'}
                    {' '}Shoal cautions show amber rather than red.
                  </p>
                </>
              )}

              <Toggle
                label="Let shoal cautions raise the alert too"
                checked={settings.alerts.includeCautions}
                onChange={(includeCautions) => patchAlerts({ includeCautions })}
              />
              <p className="sheet__hint sheet__hint--small">
                Turn this off on a shoal-strewn coast where the caution tier is
                firing constantly. The chart still marks them either way.
              </p>

              <h3 className="sheet__section">When it goes off</h3>
              <p className="sheet__hint sheet__hint--small">
                How strong the alert is at each time to run. Everything in
                between is interpolated, so it still moves smoothly — these are
                the corners of the curve, not steps.
              </p>

              <div className="stages" role="group" aria-label="Alert stages">
                <div className="stages__head">
                  <span>Time to run</span>
                  <span>Strength</span>
                </div>
                {settings.alerts.stages.map((stage, index) => (
                  <div className="stages__row" key={`stage-${index}`}>
                    <label className="stages__cell">
                      <span className="stages__label">
                        Stage {index + 1} time, seconds
                      </span>
                      <input
                        type="number"
                        inputMode="numeric"
                        min={0}
                        max={MAX_STAGE_TIME_S}
                        step={5}
                        value={Math.round(stage.timeToS)}
                        aria-label={`Stage ${index + 1} time to run, seconds`}
                        onChange={(e) => {
                          const timeToS = Number(e.target.value);
                          if (!Number.isFinite(timeToS)) return;
                          patchStage(index, { timeToS });
                        }}
                      />
                      <span className="stages__unit">{formatHorizon(stage.timeToS)}</span>
                    </label>
                    <label className="stages__cell">
                      <span className="stages__label">
                        Stage {index + 1} strength
                      </span>
                      <input
                        type="number"
                        inputMode="numeric"
                        min={0}
                        max={100}
                        step={1}
                        value={Math.round(stage.level * 100)}
                        aria-label={`Stage ${index + 1} strength, percent`}
                        onChange={(e) => {
                          const percent = Number(e.target.value);
                          if (!Number.isFinite(percent)) return;
                          patchStage(index, { level: percent / 100 });
                        }}
                      />
                      <span className="stages__unit">%</span>
                    </label>
                  </div>
                ))}
              </div>

              <p className="sheet__hint sheet__hint--small">
                Beyond {formatHorizon(settings.alerts.stages[0]?.timeToS ?? 0)} there
                is no alert at all. Inside{' '}
                {formatHorizon(
                  settings.alerts.stages[settings.alerts.stages.length - 1]?.timeToS ?? 0,
                )}{' '}
                it holds at its last strength — closer than full alert is still
                full alert.
              </p>
              <p className="sheet__hint sheet__hint--small">
                A curve that gets <em>quieter</em> as you close is worse than no
                alert, so a strength set below the one further out is raised to
                match it rather than obeyed.
              </p>

              <h3 className="sheet__section">Collision alert</h3>
              <p className="sheet__hint sheet__hint--small">
                The last few seconds, said differently. A rising click rate and a
                deepening colour convey a trend, and once the trend has run out
                there is nothing left for it to do — so inside this time the
                colour <em>flashes</em> instead. It is a different kind of signal
                on purpose: it cannot be read as more of the previous one.
              </p>

              <Toggle
                label="Flash when about to hit"
                checked={settings.alerts.collision.enabled}
                onChange={(enabled) => patchCollision({ enabled })}
              />
              {settings.alerts.collision.enabled && (
                <>
                  <NumberField
                    label="Fires within"
                    hint="Time to run at which the flashing starts."
                    value={Math.round(settings.alerts.collision.timeToS)}
                    unit="s"
                    step={1}
                    min={COLLISION_LIMITS.timeToS.min}
                    max={COLLISION_LIMITS.timeToS.max}
                    onChange={(timeToS) => patchCollision({ timeToS })}
                  />
                  <NumberField
                    label="Flash rate"
                    hint="Capped at 3 per second — see below."
                    value={settings.alerts.collision.flashHz}
                    unit="per second"
                    step={0.5}
                    min={COLLISION_LIMITS.flashHz.min}
                    max={COLLISION_LIMITS.flashHz.max}
                    onChange={(flashHz) => patchCollision({ flashHz })}
                  />
                  {!settings.alerts.tint && (
                    <p className="sheet__hint sheet__hint--small" role="alert">
                      Nothing will flash until <strong>Show the alert as a
                      colour</strong> is on — the flash is that colour
                      interrupted, not a separate thing.
                    </p>
                  )}
                  <p className="sheet__hint sheet__hint--small">
                    Dangers only. A shoal you can float over is not a collision,
                    however close it is, and spending the loudest signal in the
                    app on passable water would undo the whole caution tier.
                  </p>
                  <p className="sheet__hint sheet__hint--small">
                    The rate cannot be set above {COLLISION_LIMITS.flashHz.max} per
                    second. Faster flashing of an area this large is a
                    photosensitive-seizure risk, and that is not a decision one
                    person can make for everyone who later looks at the screen.
                  </p>
                </>
              )}

              <p className="sheet__hint sheet__hint--small" role="note">
                <strong>Quiet is not clear water.</strong> The alert can only
                hear about what the forward scan found, and the scan reads the
                chart tiles that are loaded and on screen. Nothing here replaces
                keeping a lookout.
              </p>
            </>
          )}
        </>
      )}

      {tab === 'debug' && (
        <>
          <p className="sheet__hint">
            Development aids. The chart cannot be judged from a desk — the track
            ahead and the dangers in it need a position, a course and a speed,
            and a device on a bench has none of them.
          </p>

          <Toggle
            label="Simulate position"
            checked={settings.debug.simulate}
            onChange={(simulate) => patchDebug({ simulate })}
          />
          <p className="sheet__hint sheet__hint--small">
            Tap the chart to put the boat there. It reports the heading and speed
            below instead of the GPS, and a banner says so for as long as this is
            on. Building a route still takes the tap; identifying a feature does
            not, so turn this off to go back to reading the chart.
          </p>

          {settings.debug.simulate && (
            <>
              <NumberField
                label="Heading"
                hint="Degrees true. What the simulated boat reports as course over ground."
                value={Math.round(settings.debug.headingDeg)}
                unit="°T"
                step={5}
                min={0}
                max={359}
                onChange={(headingDeg) => patchDebug({
                  headingDeg: ((headingDeg % 360) + 360) % 360,
                })}
              />
              <NumberField
                label="Speed"
                value={msToSpeed(settings.debug.speedMs, units.speed)}
                unit={UNIT_LABELS[units.speed]}
                step={0.5}
                min={0}
                max={msToSpeed(MAX_SIMULATED_SPEED_MS, units.speed)}
                onChange={(v) => patchDebug({ speedMs: speedToMs(v, units.speed) })}
              />
              <Toggle
                label="Under way"
                checked={settings.debug.underway}
                onChange={(underway) => patchDebug({ underway })}
              />
              <p className="sheet__hint sheet__hint--small">
                Advances the boat along its heading once a second, so you can
                stand towards something and watch the range close. The proximity
                alert needs this: a click rate and a tint that only mean anything
                while the range is shrinking cannot be judged from a boat parked
                next to a rock.
              </p>
              <p className="sheet__hint sheet__hint--small">
                Below about a knot there is no course to work from, so the track
                and the dangers ahead both disappear — the same rule the real GPS
                gets.
              </p>
            </>
          )}
        </>
      )}

      {tab === 'legal' && (
        <div className="legal">
          {/* The same text as the first-run gate, from the same source. A
              summary here would be a second version to keep in step, and the
              one that went stale would be the one being read. */}
          {LEGAL_SECTIONS.map((section) => (
            <section key={section.heading}>
              <h3>{section.heading}</h3>
              {section.paragraphs.map((paragraph) => (
                <p key={paragraph.slice(0, 40)}>{paragraph}</p>
              ))}
            </section>
          ))}
        </div>
      )}
    </div>
  );
}

interface NumberFieldProps {
  label: string;
  hint?: string;
  value: number | '';
  unit: string;
  step: number;
  min?: number;
  max?: number;
  allowEmpty?: boolean;
  onChange: (value: never) => void;
}

function NumberField({
  label, hint, value, unit, step, min, max, allowEmpty = false, onChange,
}: NumberFieldProps): React.JSX.Element {
  return (
    <label className="field">
      <span className="field__label">
        {label}
        <span className="field__unit">{unit}</span>
      </span>
      <input
        type="number"
        inputMode="decimal"
        value={typeof value === 'number' ? Number(value.toFixed(2)) : ''}
        step={step}
        min={min}
        max={max}
        onChange={(e) => {
          const raw = e.target.value;
          if (raw === '' && allowEmpty) { (onChange as (v: null) => void)(null); return; }
          const parsed = Number(raw);
          if (Number.isFinite(parsed)) (onChange as (v: number) => void)(parsed);
        }}
      />
      {hint && <span className="field__hint">{hint}</span>}
    </label>
  );
}

/**
 * A slider with its value shown.
 *
 * A range input rather than a number field because volume is set by ear while
 * something else is happening — nobody types 0.65 into a boat.
 */
function RangeField({
  label, value, display, min, max, step, onChange,
}: {
  label: string;
  value: number;
  display: string;
  min: number;
  max: number;
  step: number;
  onChange: (value: number) => void;
}): React.JSX.Element {
  return (
    <label className="field">
      <span className="field__label">
        {label}
        <span className="field__value">{display}</span>
      </span>
      <input
        type="range"
        value={value}
        min={min}
        max={max}
        step={step}
        onChange={(e) => onChange(Number(e.target.value))}
      />
    </label>
  );
}

function UnitChoice<T extends keyof typeof UNIT_LABELS>({
  label, options, value, onChange,
}: {
  label: string;
  options: readonly T[];
  value: T;
  onChange: (value: T) => void;
}): React.JSX.Element {
  return (
    <div className="field">
      <span className="field__label">{label}</span>
      <div className="unit-choice" role="radiogroup" aria-label={label}>
        {options.map((option) => (
          <button
            key={option}
            type="button"
            role="radio"
            aria-checked={value === option}
            className={value === option ? 'unit-choice__option unit-choice__option--on' : 'unit-choice__option'}
            onClick={() => onChange(option)}
          >
            {UNIT_LABELS[option]}
          </button>
        ))}
      </div>
    </div>
  );
}

function Toggle({
  label, checked, onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}): React.JSX.Element {
  return (
    <label className="sheet__toggle">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      {label}
    </label>
  );
}
